import React, { useEffect, useMemo, useRef, useState } from 'react';
import MapView from './MapView';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const DEFAULT_CENTER = { lat: -3.4488, lng: -79.9595 };

const loadGoogleMaps = () => {
  if (!GOOGLE_MAPS_API_KEY) {
    return Promise.reject(new Error('missing-google-maps-key'));
  }
  if (window.google?.maps) {
    return Promise.resolve(window.google);
  }
  if (window.__proviGoogleMapsPromise) {
    return window.__proviGoogleMapsPromise;
  }

  window.__proviGoogleMapsPromise = new Promise((resolve, reject) => {
    window.gm_authFailure = () => reject(new Error('google-maps-auth-failed'));
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}&libraries=places&language=es&region=EC`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error('google-maps-load-failed'));
    document.head.appendChild(script);
  });

  return window.__proviGoogleMapsPromise;
};

const toNumber = (value) => Number(value);
const hasPoint = (point) => Number.isFinite(toNumber(point?.lat)) && Number.isFinite(toNumber(point?.lng));
const asLatLng = (point) => ({ lat: toNumber(point.lat), lng: toNumber(point.lng) });

const svgIcon = (kind, fill, stroke = '#FFFFFF') => {
  const body = kind === 'car'
    ? `<path d="M10 31l4-11c1-3 4-5 7-5h14c3 0 6 2 7 5l4 11v11H10V31z" fill="${fill}" stroke="${stroke}" stroke-width="3"/><circle cx="18" cy="42" r="4" fill="${stroke}"/><circle cx="38" cy="42" r="4" fill="${stroke}"/>`
    : kind === 'person'
      ? `<circle cx="24" cy="15" r="7" fill="${fill}" stroke="${stroke}" stroke-width="3"/><path d="M13 45c2-12 7-18 11-18s9 6 11 18" fill="${fill}" stroke="${stroke}" stroke-width="3" stroke-linecap="round"/>`
      : `<path d="M24 4c-9 0-16 7-16 16 0 12 16 28 16 28s16-16 16-28C40 11 33 4 24 4z" fill="${fill}" stroke="${stroke}" stroke-width="3"/><circle cx="24" cy="20" r="6" fill="${stroke}"/>`;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">${body}</svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const buildMarkerIcon = (google, kind, color) => ({
  url: svgIcon(kind, color),
  scaledSize: new google.maps.Size(42, 42),
  anchor: new google.maps.Point(21, 42)
});

const getRoutePoints = ({ pickup, dropoff, providerLocation, rideStatus, routePhase }) => {
  if (routePhase === 'client_tracking') {
    return { origin: null, destination: null, phase: 'client_tracking' };
  }
  if (routePhase === 'driver_to_pickup' || ['accepted', 'driver_on_way'].includes(rideStatus)) {
    return { origin: providerLocation, destination: pickup, phase: 'driver_to_pickup' };
  }
  if (routePhase === 'driver_to_dropoff') {
    return { origin: providerLocation, destination: dropoff, phase: 'driver_to_dropoff' };
  }
  if (routePhase === 'pickup_to_dropoff' || rideStatus === 'arrived') {
    return { origin: pickup, destination: dropoff, phase: 'pickup_to_dropoff' };
  }
  if (rideStatus === 'in_progress') {
    return { origin: hasPoint(providerLocation) ? providerLocation : pickup, destination: dropoff, phase: 'pickup_to_dropoff' };
  }
  return { origin: pickup, destination: dropoff, phase: routePhase || 'pickup_to_dropoff' };
};

const fitMapToPoints = (google, map, points) => {
  const valid = points.filter(hasPoint);
  if (!valid.length) {
    map.setCenter(DEFAULT_CENTER);
    map.setZoom(13);
    return;
  }
  if (valid.length === 1) {
    map.setCenter(asLatLng(valid[0]));
    map.setZoom(15);
    return;
  }
  const bounds = new google.maps.LatLngBounds();
  valid.forEach((point) => bounds.extend(asLatLng(point)));
  map.fitBounds(bounds, 64);
};

const GoogleRideTrackingMap = ({
  pickup,
  pickup_location: pickupLocation,
  dropoff,
  dropoff_location: dropoffLocation,
  providerLocation,
  provider_location: providerLocationAlias,
  rideStatus,
  ride_status: rideStatusAlias,
  routePhase,
  mode,
  height = '260px',
  viewer = 'client',
  onEtaChange
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const directionsServiceRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const markersRef = useRef({});
  const requestRef = useRef(0);
  const etaCallbackRef = useRef(onEtaChange);
  const [ready, setReady] = useState(false);
  const [fallback, setFallback] = useState(false);
  const [message, setMessage] = useState('');
  const resolvedPickup = pickup || pickupLocation;
  const resolvedDropoff = dropoff || dropoffLocation;
  const resolvedProviderLocation = providerLocation || providerLocationAlias;
  const resolvedRideStatus = rideStatus || rideStatusAlias;
  const resolvedRoutePhase = mode || routePhase;

  const route = useMemo(
    () => getRoutePoints({
      pickup: resolvedPickup,
      dropoff: resolvedDropoff,
      providerLocation: resolvedProviderLocation,
      rideStatus: resolvedRideStatus,
      routePhase: resolvedRoutePhase
    }),
    [resolvedPickup, resolvedDropoff, resolvedProviderLocation, resolvedRideStatus, resolvedRoutePhase]
  );

  useEffect(() => {
    etaCallbackRef.current = onEtaChange;
  }, [onEtaChange]);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then((google) => {
        if (cancelled || !mapRef.current) return;
        const map = new google.maps.Map(mapRef.current, {
          center: hasPoint(resolvedProviderLocation) ? asLatLng(resolvedProviderLocation) : hasPoint(resolvedPickup) ? asLatLng(resolvedPickup) : DEFAULT_CENTER,
          zoom: 14,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          gestureHandling: 'cooperative'
        });
        mapInstanceRef.current = map;
        directionsServiceRef.current = new google.maps.DirectionsService();
        directionsRendererRef.current = new google.maps.DirectionsRenderer({
          map,
          suppressMarkers: true,
          preserveViewport: false,
          polylineOptions: {
            strokeColor: '#D946EF',
            strokeWeight: 7,
            strokeOpacity: 0.95
          }
        });
        setReady(true);
      })
      .catch(() => {
        setFallback(true);
        setMessage('Google Maps no esta disponible. Se muestra el mapa alternativo.');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready || !mapInstanceRef.current || !window.google?.maps) return;
    const google = window.google;
    const map = mapInstanceRef.current;

    const setMarker = (key, point, label, kind, color) => {
      if (!hasPoint(point)) {
        markersRef.current[key]?.setMap(null);
        delete markersRef.current[key];
        return;
      }
      const position = asLatLng(point);
      if (!markersRef.current[key]) {
        markersRef.current[key] = new google.maps.Marker({
          map,
          position,
          label: {
            text: label,
            color: '#FFFFFF',
            fontSize: '11px',
            fontWeight: '700'
          },
          icon: buildMarkerIcon(google, kind, color)
        });
      } else {
        markersRef.current[key].setPosition(position);
      }
    };

    setMarker('provider', resolvedProviderLocation, viewer === 'provider' ? 'Yo' : 'Taxi', 'car', '#FF007F');
    setMarker('pickup', resolvedPickup, 'Cliente', 'person', '#00F0FF');
    setMarker('dropoff', resolvedDropoff, 'Destino', 'pin', '#EF4444');
    fitMapToPoints(google, map, [resolvedProviderLocation, resolvedPickup, resolvedDropoff]);
  }, [ready, resolvedPickup, resolvedDropoff, resolvedProviderLocation, viewer]);

  useEffect(() => {
    if (!ready || !directionsServiceRef.current || !directionsRendererRef.current) return;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;

    if (!hasPoint(route.origin) || !hasPoint(route.destination)) {
      directionsRendererRef.current.set('directions', null);
      const text = !hasPoint(resolvedProviderLocation) && ['accepted', 'driver_on_way', 'in_progress'].includes(resolvedRideStatus)
        ? 'Esperando ubicacion del conductor'
        : '';
      setMessage(text);
      if (etaCallbackRef.current) etaCallbackRef.current(null);
      return;
    }

    setMessage('');
    directionsServiceRef.current.route(
      {
        origin: asLatLng(route.origin),
        destination: asLatLng(route.destination),
        travelMode: window.google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: false
      },
      (result, status) => {
        if (requestId !== requestRef.current) return;
        if (status !== 'OK' || !result?.routes?.[0]) {
          setMessage('No se pudo calcular ETA real.');
          if (etaCallbackRef.current) etaCallbackRef.current(null);
          return;
        }
        directionsRendererRef.current.setDirections(result);
        const leg = result.routes[0].legs?.[0];
        if (!leg) return;
        const eta = {
          minutes: Math.max(1, Math.round((leg.duration?.value || 0) / 60)),
          distance_km: Number(((leg.distance?.value || 0) / 1000).toFixed(2)),
          phase: route.phase
        };
        if (etaCallbackRef.current) etaCallbackRef.current(eta);
      }
    );
  }, [ready, route, resolvedProviderLocation, resolvedRideStatus]);

  if (fallback) {
    return (
      <div className="space-y-2">
        {message && <p className="text-xs text-yellow-300 bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3">{message}</p>}
        <MapView pickup={resolvedPickup} dropoff={resolvedDropoff} vehiclePosition={resolvedProviderLocation} vehicleIcon="car" vehicleColor="#FF007F" height={height} />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {message && <p className="text-xs text-yellow-300 bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3">{message}</p>}
      <div ref={mapRef} style={{ height, width: '100%', borderRadius: '1.25rem', overflow: 'hidden' }} data-testid="google-ride-tracking-map" />
    </div>
  );
};

export default GoogleRideTrackingMap;
